/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2025, Bulb Inc. For more information, please email contact@hellobulb.com.
*/
import {
  Component, Inject, Input, OnChanges, OnInit,
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { GroupHttpService } from '../group.service';
import { BULB_BUSY_COVER_SERVICE } from '../../angularjs.providers';

/**
 * Component for managing and displaying pinned units of a group.
 * - Loads all group units and pinned unit IDs from backend.
 * - Builds an ordered pinned unit list.
 * - Filters pinned units based on search text.
 * - Sorts pinned units dynamically based on selected property and direction.
 */

@Component({
  selector: 'bulb-pinned-units',
  templateUrl: './pinned-units.component.html',
  styleUrls: ['./pinned-units.component.scss'],
  standalone: false,
})
export class PinnedUnitsComponent implements OnInit, OnChanges {

  /**
   * Active tab name.
   */
  @Input() activeTab: string;

  /**
   * Group whose pinned units are being displayed.
   */
  @Input() group: any;

  /**
   * Property name used to sort pinned units.
   */
  // @Input() sortBy: string;

  /**
   * Sorting order flag.
   * - `true`: ascending
   * - `false`: descending
   */
  // @Input() sortAscending: boolean;

  /**
   * Search term used to filter pinned units by name or owner display name.
   */
  @Input() searchText: string;

  /**
   * List of all units belonging to the group.
   */
  units: any[] = [];

  /**
   * Array of pinned unit IDs for the current group.
   */
  pinnedUnitIds: string[] = [];

  /**
   * Ordered list of pinned unit objects, built from `pinnedUnitIds`.
   */
  pinnedUnits: any[] = [];

  /**
   * Filtered and sorted list of pinned units displayed in the UI.
   */
  filteredPinnedUnits: any[] = [];

  constructor(
    private groupService: GroupHttpService,
    @Inject(BULB_BUSY_COVER_SERVICE) private bulbBusyCoverService: any,
    @Inject('BulbErrorService') private bulbErrorService: any,
    private translocoService: TranslocoService,
  ) {
  }

  /**
   * Handles the dragenter event on a separator element.
   * Adds the `highlight-separator` class when a draggable item enters
   * the separator zone so the user gets visual feedback.
   *
   * @param event - The native DragEvent triggered when a draggable
   *                item enters the separator element.
   */
  onDragEnterSeparator(event: DragEvent) {
    event.preventDefault();
    (event.currentTarget as HTMLElement).classList.add('highlight-separator');
  }

  /**
   * Handles the dragleave event on a separator element.
   * Removes the `highlight-separator` class when the draggable item
   * leaves the separator zone, but ensures that it is not removed
   * if the pointer is still inside a child element (e.g., drag-handle-line).
   *
   * @param event - The native DragEvent triggered when a draggable
   *                item leaves the separator element.
   */
  onDragLeaveSeparator(event: DragEvent) {
    const current = event.currentTarget as HTMLElement;
    const related = event.relatedTarget as Node | null;

    if (!related || !current.contains(related)) {
      current.classList.remove('highlight-separator');
    }
  }

  /**
   * Angular lifecycle hook triggered when input properties change.
   * Applies filtering and sorting to pinned units.
   */
  ngOnChanges(): void {
    this.updateUnits();
    this.filterPinUnits();
    // this.sortByProperty();
  }

  /**
   * Angular lifecycle hook triggered on component initialization.
   * Fetches all units and pinned unit IDs, then builds ordered pinned unit list.
   */
  ngOnInit(): void {
    this.group = JSON.parse(this.group);
    this.updateUnits();
  }

  /**
   * Filters pinned units based on `searchText`.
   * Matches against `name` or `ownerDisplayName`.
   */
  filterPinUnits() {
    if (!this.searchText) {
      this.filteredPinnedUnits = [...this.pinnedUnits];
      return;
    }
    this.filteredPinnedUnits = [...this.pinnedUnits.filter(
      (unit) => (unit.name && unit.name.toLowerCase().includes(this.searchText.toLowerCase()))
      || (unit.ownerDisplayName && unit.ownerDisplayName.toLowerCase().includes(this.searchText.toLowerCase())),
    )];
  }

  /**
   * Sorts `filteredPinnedUnits` by the configured `sortBy` property and `sortAscending` flag.
   */
  // sortByProperty() {
  //   this.filteredPinnedUnits.sort((a, b) => {
  //     let aVal = a[this.sortBy];
  //     let bVal = b[this.sortBy];
  //
  //     // Handle missing or null values — they go to the end
  //     const aMissing = aVal === undefined || aVal === null;
  //     const bMissing = bVal === undefined || bVal === null;
  //
  //     if (aMissing && bMissing) return 0;
  //     if (aMissing) return 1;
  //     if (bMissing) return -1;
  //
  //     // Convert strings to lowercase for case-insensitive comparison
  //     if (typeof aVal === 'string') aVal = aVal.toLowerCase();
  //     if (typeof bVal === 'string') bVal = bVal.toLowerCase();
  //
  //     // Compare values based on sortAscending boolean
  //     if (aVal < bVal) return this.sortAscending ? -1 : 1;
  //     if (aVal > bVal) return this.sortAscending ? 1 : -1;
  //     return 0;
  //   });
  // }

  /**
   * Drag-and-drop handler for allowing units to be dropped on a target index.
   * @param event Drag event
   */
  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer!.dropEffect = 'move';
  }

  /**
   * Handles drop event on a specific pinned unit position.
   * Reorders pinned units and persists new order to backend.
   *
   * @param event Drag event
   * @param index Target index to insert the dragged unit
   */
  onDrop(event: DragEvent, index: number) {
    event.preventDefault();
    event.stopPropagation();
    const json = event.dataTransfer?.getData('application/json');
    if (!json) return;

    const data = JSON.parse(json);
    const draggedUnit = data.unit;

    const wrapper = event.currentTarget as HTMLElement;
    const blockWrapper = wrapper.querySelector('.bulb-block-wrapper');

    if (!blockWrapper) {
      console.warn('Could not find .bulb-block-wrapper inside wrapper');
      return;
    }

    const rect = blockWrapper.getBoundingClientRect();
    const isAfter = event.clientX >= rect.left + rect.width / 2;
    const insertAt = isAfter ? index + 1 : index;
    const stopBusyIndicatorCallback = this.bulbBusyCoverService.showBusy();
    const groupId = typeof this.group === 'object' ? this.group.id : JSON.parse(this.group).id;
    this.groupService.pinUnits(groupId, insertAt, draggedUnit.id, this.activeTab !== 'assignments').subscribe(() => {
      stopBusyIndicatorCallback();
      this.groupService.setActiveTab(this.activeTab);
      window.dispatchEvent(new CustomEvent('refreshView'));
    }, (error) => {
      stopBusyIndicatorCallback();
      console.error('Error pinning unit:', error);
      if (error.status === 403) {
        this.bulbErrorService.showError(this.translocoService.translate('pinnedUnits.error'));
      } else {
        this.bulbErrorService.showError(this.translocoService.translate('pinnedUnits.retryError'));
      }
    });
  }

  /**
   * Handles the drag start event for a draggable unit tile.
   *
   * - Clones the dragged element to use as the drag image.
   * - Sets the payload (`childUnit`) as application/json in `dataTransfer`.
   * - Cleans up the temporary drag image on `dragend` and dispatches `refreshView`.
   *
   * @param event   Drag event carrying the dataTransfer object.
   * @param element The DOM element being dragged.
   * @param childUnit The data payload representing the dragged unit.
   */
  onDragStart(event: DragEvent, element: HTMLElement, childUnit: any) {
    const payload = {
      unit: childUnit,
    };
    const dragImage = element.cloneNode(true) as HTMLElement;
    const bottomSection = dragImage.querySelector('.bulb-tile-footer-container');
    if (bottomSection) {
      (bottomSection as HTMLElement).style.display = 'none';
    }

    document.body.appendChild(dragImage);
    event.dataTransfer.setDragImage(dragImage, 0, 0);
    event.dataTransfer.setData('application/json', JSON.stringify(payload));
    event.dataTransfer.effectAllowed = 'move';

    event.target.addEventListener('dragend', () => {
      if (document.body.contains(dragImage)) {
        document.body.removeChild(dragImage);
      }
      window.dispatchEvent(new CustomEvent('refreshView'));
    });
  }

  /**
   * Handles drop event in the pinned container (outside specific unit positions).
   * Adds the dropped unit at the end of the pinned units list.
   *
   * @param event Drag event
   */
  onDropContainer(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    const json = event.dataTransfer?.getData('application/json');
    if (!json) return;

    const data = JSON.parse(json);
    const draggedUnit = data.unit;
    const index = this.pinnedUnits.length;
    const stopBusyIndicatorCallback = this.bulbBusyCoverService.showBusy();
    const groupId = typeof this.group === 'object' ? this.group.id : JSON.parse(this.group).id;
    this.groupService.pinUnits(groupId, index, draggedUnit.id, this.activeTab !== 'assignments').subscribe(() => {
      stopBusyIndicatorCallback();
      this.groupService.setActiveTab(this.activeTab);
      window.dispatchEvent(new CustomEvent('refreshView'));
    }, (error) => {
      stopBusyIndicatorCallback();
      console.error('Error pinning unit:', error);
      if (error.status === 403) {
        this.bulbErrorService.showError(this.translocoService.translate('pinnedUnits.error'));
      } else {
        this.bulbErrorService.showError(this.translocoService.translate('pinnedUnits.retryError'));
      }
    });
  }

  /**
   * Updates the units and pinned units based on the active tab.
   *
   * If the active tab is 'assignments', it loads assignment-related data;
   * otherwise, it loads content-related data.
   */
  updateUnits() {
    const isAssignmentTabActive = this.activeTab === 'assignments';
    this.units = isAssignmentTabActive ? this.group.assignments : this.group.children;
    if (this.units) {
      this.pinnedUnitIds = isAssignmentTabActive ? this.group.pinnedAssignmentIds : this.group.pinnedContentIds;
      // build map for quick lookup
      const unitMap = new Map(this.units.map((u) => [u.id, u]));

      // pick only matching objects in the given order
      this.pinnedUnits = this.pinnedUnitIds.map((id) => unitMap.get(id)!)
        .filter((unit) => unit !== undefined);
      this.filteredPinnedUnits = [...this.pinnedUnits];
    }
  }
}
